/**
 * 错误处理和用户反馈服务
 */
import { message, notification, Modal } from 'antd';

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof navigator !== 'undefined';
const isOnline = (): boolean => (isBrowser() ? navigator.onLine : true);

// 错误类型枚举
export enum ErrorType {
  NETWORK = 'network',
  VALIDATION = 'validation',
  PERMISSION = 'permission',
  SERVER = 'server',
  UNKNOWN = 'unknown'
}

// 错误级别枚举
export enum ErrorLevel {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

// 错误信息接口
export interface ErrorInfo {
  type: ErrorType;
  level: ErrorLevel;
  message: string;
  details?: any;
  timestamp: number;
  userId?: string;
  action?: string;
  stack?: string;
}

// 用户反馈配置
interface FeedbackConfig {
  showNotification: boolean;
  showMessage: boolean;
  showModal: boolean;
  autoReport: boolean;
  retryable: boolean;
}

class ErrorService {
  private errorLog: ErrorInfo[] = [];
  private maxLogSize = 100;
  private reportEndpoint = '/errors/report';

  /**
   * 处理错误
   */
  handleError(
    error: Error | any,
    context?: {
      type?: ErrorType;
      level?: ErrorLevel;
      action?: string;
      userId?: string;
    },
    config?: Partial<FeedbackConfig>
  ): void {
    const errorInfo = this.createErrorInfo(error, context);
    this.logError(errorInfo);

    const feedbackConfig: FeedbackConfig = {
      showNotification: true,
      showMessage: false,
      showModal: false,
      autoReport: true,
      retryable: false,
      ...config
    };

    this.showUserFeedback(errorInfo, feedbackConfig);

    if (feedbackConfig.autoReport) {
      this.reportError(errorInfo);
    }
  }

  /**
   * 创建错误信息对象
   */
  private createErrorInfo(
    error: Error | any,
    context?: {
      type?: ErrorType;
      level?: ErrorLevel;
      action?: string;
      userId?: string;
    }
  ): ErrorInfo {
    let type = context?.type || ErrorType.UNKNOWN;
    let level = context?.level || ErrorLevel.ERROR;
    let msg = '发生未知错误';

    // 根据错误类型自动判断
    if (error?.response) {
      // HTTP 错误
      const status = error.response.status;
      if (status >= 400 && status < 500) {
        type = status === 401 || status === 403 ? ErrorType.PERMISSION : ErrorType.VALIDATION;
        level = ErrorLevel.WARNING;
      } else if (status >= 500) {
        type = ErrorType.SERVER;
        level = ErrorLevel.ERROR;
      }
      msg = error.response.data?.message || `HTTP ${status} 错误`;
    } else if (error?.code === 'NETWORK_ERROR' || !isOnline()) {
      type = ErrorType.NETWORK;
      level = ErrorLevel.WARNING;
      msg = '网络连接失败，请检查网络设置';
    } else if (error?.message) {
      msg = error.message;
    }

    return {
      type,
      level,
      message: msg,
      details: error,
      timestamp: Date.now(),
      userId: context?.userId,
      action: context?.action,
      stack: error?.stack
    };
  }

  /**
   * 记录错误日志
   */
  private logError(errorInfo: ErrorInfo): void {
    console.error('Error logged:', errorInfo);

    this.errorLog.unshift(errorInfo);

    // 限制日志大小
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxLogSize);
    }

    // 存储到本地存储
    try {
      if (isBrowser()) {
        localStorage.setItem('error_log', JSON.stringify(this.errorLog.slice(0, 20)));
      }
    } catch {
      console.warn('无法保存错误日志到本地存储');
    }
  }

  /**
   * 显示用户反馈
   */
  private showUserFeedback(errorInfo: ErrorInfo, config: FeedbackConfig): void {
    const { message: msg, level, type } = errorInfo;

    if (config.showMessage) {
      switch (level) {
        case ErrorLevel.INFO:
          message.info(msg);
          break;
        case ErrorLevel.WARNING:
          message.warning(msg);
          break;
        case ErrorLevel.ERROR:
        case ErrorLevel.CRITICAL:
          message.error(msg);
          break;
      }
    }

    if (config.showNotification) {
      const notificationConfig = {
        message: this.getErrorTitle(type, level),
        description: msg,
        duration: level === ErrorLevel.CRITICAL ? 0 : 4.5,
      };

      switch (level) {
        case ErrorLevel.INFO:
          notification.info(notificationConfig);
          break;
        case ErrorLevel.WARNING:
          notification.warning(notificationConfig);
          break;
        case ErrorLevel.ERROR:
        case ErrorLevel.CRITICAL:
          notification.error({
            ...notificationConfig,
            btn: config.retryable ? {
              text: '重试',
              onClick: () => this.handleRetry(errorInfo)
            } : undefined
          });
          break;
      }
    }

    if (config.showModal) {
      Modal.error({
        title: this.getErrorTitle(type, level),
        content: msg,
        okText: '确定',
        onOk: config.retryable ? () => this.handleRetry(errorInfo) : undefined
      });
    }
  }

  /**
   * 获取错误标题
   */
  private getErrorTitle(type: ErrorType, level: ErrorLevel): string {
    const typeMap = {
      [ErrorType.NETWORK]: '网络错误',
      [ErrorType.VALIDATION]: '数据验证错误',
      [ErrorType.PERMISSION]: '权限错误',
      [ErrorType.SERVER]: '服务器错误',
      [ErrorType.UNKNOWN]: '系统错误'
    };

    const levelMap = {
      [ErrorLevel.INFO]: '提示',
      [ErrorLevel.WARNING]: '警告',
      [ErrorLevel.ERROR]: '错误',
      [ErrorLevel.CRITICAL]: '严重错误'
    };

    return `${levelMap[level]} - ${typeMap[type]}`;
  }

  /**
   * 处理重试
   */
  private handleRetry(errorInfo: ErrorInfo): void {
    // 这里可以实现重试逻辑
    console.log('重试操作:', errorInfo);
    message.info('正在重试...');
  }

  /**
   * 上报错误到服务器
   */
  private async reportError(errorInfo: ErrorInfo): Promise<void> {
    try {
      // 只上报严重错误和服务器错误
      if (errorInfo.level === ErrorLevel.CRITICAL || errorInfo.type === ErrorType.SERVER) {
        await fetch(this.reportEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...errorInfo,
            userAgent: isBrowser() ? navigator.userAgent : 'unknown',
            url: isBrowser() ? window.location.href : 'unknown'
          })
        });
      }
    } catch (e) {
      console.warn('错误上报失败:', e);
    }
  }

  /**
   * 获取错误日志
   */
  getErrorLog(): ErrorInfo[] {
    return [...this.errorLog];
  }

  /**
   * 清空错误日志
   */
  clearErrorLog(): void {
    this.errorLog = [];
    if (isBrowser()) {
      localStorage.removeItem('error_log');
    }
  }

  /**
   * 获取错误统计
   */
  getErrorStats(): {
    total: number;
    byType: Record<ErrorType, number>;
    byLevel: Record<ErrorLevel, number>;
    recent: number; // 最近1小时的错误数
  } {
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    const recentErrors = this.errorLog.filter(error => error.timestamp > oneHourAgo);

    const byType = Object.values(ErrorType).reduce((acc, type) => {
      acc[type] = this.errorLog.filter(error => error.type === type).length;
      return acc;
    }, {} as Record<ErrorType, number>);

    const byLevel = Object.values(ErrorLevel).reduce((acc, level) => {
      acc[level] = this.errorLog.filter(error => error.level === level).length;
      return acc;
    }, {} as Record<ErrorLevel, number>);

    return {
      total: this.errorLog.length,
      byType,
      byLevel,
      recent: recentErrors.length
    };
  }

  /**
   * 创建错误边界组件类
   * 返回错误边界组件的构造函数
   */
  createErrorBoundaryClass() {
    const self = this;

    return class ErrorBoundary {
      constructor() {
        this.hasError = false;
        this.error = null;
      }

      static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
      }

      componentDidCatch(error: Error) {
        self.handleError(error, {
          type: ErrorType.UNKNOWN,
          level: ErrorLevel.CRITICAL,
          action: 'component_render'
        }, {
          showNotification: true,
          showMessage: false,
          showModal: false,
          autoReport: true,
          retryable: false
        });
      }

      render() {
        if (this.hasError) {
          // 返回错误页面的HTML字符串或者触发页面重载
          if (isBrowser()) {
            window.location.reload();
          }
          return null;
        }
        return null; // 正常情况下由React处理
      }
    };
  }
}

// 创建全局错误服务实例
export const errorService = new ErrorService();

// 全局错误处理器
if (isBrowser()) {
  window.addEventListener('error', (event) => {
    errorService.handleError(event.error, {
      type: ErrorType.UNKNOWN,
      level: ErrorLevel.ERROR,
      action: 'global_error'
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    errorService.handleError(event.reason, {
      type: ErrorType.UNKNOWN,
      level: ErrorLevel.ERROR,
      action: 'unhandled_promise_rejection'
    });
  });
}

export default errorService;
